import React, { useState } from 'react';
import './OrderPanel.css';

const OrderPanel = ({ 
  order, 
  onUpdateQuantity, 
  onRemoveItem, 
  onClearOrder, 
  totalAmount,
  onCheckout,
  onHoldOrder,
  onLoadHeldOrder,
  heldOrders = []
}) => {
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [splitPayment, setSplitPayment] = useState(false);
  const [cashAmount, setCashAmount] = useState('');
  const [cardAmount, setCardAmount] = useState('');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentNotes, setPaymentNotes] = useState('');
  const [error, setError] = useState('');
  const [showHoldModal, setShowHoldModal] = useState(false);
  const [holdCustomerName, setHoldCustomerName] = useState('');
  const [showHeldOrdersModal, setShowHeldOrdersModal] = useState(false);
  
  // Card payment details state
  const [cardDetails, setCardDetails] = useState({
    transaction_id: '',
    card_last_four: '',
    card_brand: 'visa'
  });

  const cardBrands = [
    { value: 'visa', label: 'Visa' },
    { value: 'mastercard', label: 'MasterCard' },
    { value: 'amex', label: 'American Express' },
    { value: 'discover', label: 'Discover' },
    { value: 'diners', label: 'Diners Club' },
    { value: 'jcb', label: 'JCB' },
    { value: 'unionpay', label: 'UnionPay' }
  ];

  const handleUpdateQuantity = (itemId, newQuantity) => {
    if (newQuantity < 1) {
      onRemoveItem(itemId);
      return;
    }

    const item = order.find(item => item.id === itemId);
    if (!item) return;

    const availableStock = item.variant?.stock_quantity || item.stock_quantity || 0;

    if (newQuantity > availableStock) {
      setError(`Only ${availableStock} items available in stock for ${item.name}`);
      setTimeout(() => setError(''), 3000);
      return;
    }

    onUpdateQuantity(itemId, newQuantity);
  };

  const formatCurrency = (amount) => {
    const numericAmount = parseFloat(amount) || 0;
    return `LKR ${numericAmount.toLocaleString('en-LK', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  const handleCheckout = () => {
    if (order.length === 0) {
      setError('Please add items to the order');
      setTimeout(() => setError(''), 3000);
      return;
    }
    
    // Reset payment details when opening modal
    setCashAmount('');
    setCardAmount('');
    setCardDetails({
      transaction_id: '',
      card_last_four: '',
      card_brand: 'visa'
    });
    setPaymentNotes('');
    
    setShowPaymentModal(true);
  };

  const validateCardDetails = () => {
    if (!splitPayment && paymentMethod === 'card') {
      if (!cardDetails.transaction_id.trim()) {
        setError('Transaction ID is required for card payments');
        return false;
      }
      if (!cardDetails.card_last_four || cardDetails.card_last_four.length !== 4) {
        setError('Please enter valid last 4 digits of card');
        return false;
      }
      if (!cardDetails.card_brand) {
        setError('Please select card brand');
        return false;
      }
    }
    
    if (splitPayment) {
      if (!cardDetails.transaction_id.trim()) {
        setError('Transaction ID is required for split payments');
        return false;
      }
      if (!cardDetails.card_last_four || cardDetails.card_last_four.length !== 4) {
        setError('Please enter valid last 4 digits of card');
        return false;
      }
      if (!cardDetails.card_brand) {
        setError('Please select card brand');
        return false;
      }
    }
    
    return true;
  };

  const processPayment = () => {
    // Validate card details if needed
    if ((!splitPayment && paymentMethod === 'card') || splitPayment) {
      if (!validateCardDetails()) {
        setTimeout(() => setError(''), 3000);
        return;
      }
    }

    let paymentData;

    if (splitPayment) {
      const cash = parseFloat(cashAmount) || 0;
      const card = parseFloat(cardAmount) || 0;
      
      if (cash + card < totalAmount) {
        setError('Split payment amounts are less than total amount');
        setTimeout(() => setError(''), 3000);
        return;
      }

      paymentData = {
        payment_method: 'split',
        cash_amount: cash,
        card_amount: card,
        tender_amount: parseFloat(cashAmount) || 0,
        change_amount: calculateChange(),
        total_amount: totalAmount,
        notes: paymentNotes,
        // Card details for split payment
        transaction_id: cardDetails.transaction_id,
        card_last_four: cardDetails.card_last_four,
        card_brand: cardDetails.card_brand,
        split_transaction_id: cardDetails.transaction_id, // Use same transaction ID for split
        items: order.map(item => ({
          product_id: item.base_product_id || item.id,
          variant_id: item.variant?.id || null,
          quantity: item.quantity,
          price: item.price,
          barcode: item.barcode || null,
        }))
      };
    } else {
      paymentData = {
        payment_method: paymentMethod,
        tender_amount: paymentMethod === 'cash' ? (parseFloat(cashAmount) || totalAmount) : totalAmount,
        change_amount: paymentMethod === 'cash' ? calculateChange() : 0,
        total_amount: totalAmount,
        notes: paymentNotes,
        items: order.map(item => ({
          product_id: item.base_product_id || item.id,
          variant_id: item.variant?.id || null,
          quantity: item.quantity,
          price: item.price,
          barcode: item.barcode || null,
        }))
      };

      // Add card details for card payments
      if (paymentMethod === 'card') {
        paymentData.transaction_id = cardDetails.transaction_id;
        paymentData.card_last_four = cardDetails.card_last_four;
        paymentData.card_brand = cardDetails.card_brand;
      }

      // Add transaction ID for other electronic payments
      if (['bank_transfer', 'mobile'].includes(paymentMethod)) {
        paymentData.transaction_id = cardDetails.transaction_id || `TXN_${Date.now()}`;
      }
    }

    onCheckout(paymentData);
    setShowPaymentModal(false);
    resetPaymentForm();
  };

  const resetPaymentForm = () => {
    setCashAmount('');
    setCardAmount('');
    setPaymentNotes('');
    setSplitPayment(false);
    setCardDetails({
      transaction_id: '',
      card_last_four: '',
      card_brand: 'visa'
    });
  };

  const calculateChange = () => {
    if (splitPayment) {
      const cash = parseFloat(cashAmount) || 0;
      return cash - (parseFloat(cashAmount) || 0); // For split, change is based on cash portion
    } else {
      const tender = parseFloat(cashAmount) || 0;
      return tender - totalAmount;
    }
  };

  const getPaymentMethodLabel = (method) => {
    const labels = {
      cash: 'Cash',
      card: 'Credit/Debit Card',
      bank_transfer: 'Bank Transfer',
      mobile: 'Mobile Payment',
      uber: 'Uber',
      pickme: 'PickMe',
      split: 'Split Payment'
    };
    return labels[method] || method;
  };

  const isElectronicPayment = (method) => {
    return ['card', 'bank_transfer', 'mobile', 'split'].includes(method);
  };

  const isDeliveryPlatform = (method) => {
    return method === 'uber' || method === 'pickme';
  };

  const handleCardLastFourChange = (value) => {
    // Only allow numbers and limit to 4 digits
    const numbersOnly = value.replace(/\D/g, '').slice(0, 4);
    setCardDetails(prev => ({
      ...prev,
      card_last_four: numbersOnly
    }));
  };

  const handleHoldOrder = () => {
    if (order.length === 0) {
      setError('Please add items to the order before holding');
      setTimeout(() => setError(''), 3000);
      return;
    }
    setShowHoldModal(true);
  };

  const confirmHoldOrder = () => {
    if (!holdCustomerName.trim()) {
      setError('Please enter customer name for held order');
      setTimeout(() => setError(''), 3000);
      return;
    }

    onHoldOrder(holdCustomerName);
    setShowHoldModal(false);
    setHoldCustomerName('');
  };

  const loadHeldOrder = (heldOrder) => {
    onLoadHeldOrder(heldOrder);
    setShowHeldOrdersModal(false);
  };

  const deleteHeldOrder = (orderId) => {
    if (window.confirm('Are you sure you want to delete this held order?')) {
      const updatedHeldOrders = heldOrders.filter(order => order.id !== orderId);
      localStorage.setItem('heldOrders', JSON.stringify(updatedHeldOrders));
      window.dispatchEvent(new Event('storage'));
    }
  };

  return (
    <div className="order-panel">
      <div className="order-header">
        <h2>Current Order</h2>
        <div className="order-actions">
          <button 
            className="hold-order-btn"
            onClick={handleHoldOrder}
            disabled={order.length === 0}
          >
            Hold Order
          </button>
          <button 
            className="held-orders-btn"
            onClick={() => setShowHeldOrdersModal(true)}
          >
            Held Orders ({heldOrders.length})
          </button>
          <button 
            className="clear-order-btn"
            onClick={onClearOrder}
            disabled={order.length === 0}
          >
            Clear All
          </button>
        </div>
      </div>

      {/* Error Message Display */}
      {error && (
        <div className="error-message order-error">
          {error}
          <button onClick={() => setError('')} className="close-error">×</button>
        </div>
      )}
      
      <div className="order-items-container">
        <div className="order-items">
          {order.length === 0 ? (
            <div className="empty-order">No items in order</div>
          ) : (
            order.map(item => {
              const availableStock = item.variant?.stock_quantity || item.stock_quantity || 0;
              const isMaxStock = item.quantity >= availableStock;
              
              return (
                <div key={item.id} className={`order-item ${isMaxStock ? 'max-stock' : ''}`}>
                  <div className="item-info">
                    <span className="item-name">{item.name}</span>
                    <span className="item-price">{formatCurrency(item.price)} each</span>
                    {isMaxStock && (
                      <span className="stock-warning">Max: {availableStock} in stock</span>
                    )}
                  </div>
                  <div className="item-controls">
                    <button 
                      className="quantity-btn"
                      onClick={() => handleUpdateQuantity(item.id, item.quantity - 1)}
                      data-action="-"
                    >
                      -
                    </button>
                    <span className="quantity">{item.quantity}</span>
                    <button 
                      className="quantity-btn"
                      onClick={() => handleUpdateQuantity(item.id, item.quantity + 1)}
                      disabled={isMaxStock}
                      data-action="+"
                    >
                      +
                    </button>
                    <button 
                      className="remove-btn"
                      onClick={() => onRemoveItem(item.id)}
                    >
                      ×
                    </button>
                  </div>
                  <div className="item-total">
                    {formatCurrency(item.price * item.quantity)}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
      
      <div className="order-footer">
        <div className="order-total">
          <div className="total-line grand-total">
            <span>Total Amount:</span>
            <span>{formatCurrency(totalAmount)}</span>
          </div>
        </div>
        
        <button 
          className="checkout-btn"
          onClick={handleCheckout}
          disabled={order.length === 0}
        >
          PROCESS PAYMENT
        </button>
      </div>

      {/* Hold Order Modal */}
      {showHoldModal && (
        <div className="modal-overlay">
          <div className="hold-modal">
            <div className="modal-header">
              <h3>Hold Order</h3>
              <button 
                className="close-modal"
                onClick={() => setShowHoldModal(false)}
              >
                ×
              </button>
            </div>
            
            <div className="hold-details">
              <div className="hold-total">
                Total: {formatCurrency(totalAmount)}
              </div>
              
              <div className="hold-customer">
                <label>Customer Name:</label>
                <input
                  type="text"
                  value={holdCustomerName}
                  onChange={(e) => setHoldCustomerName(e.target.value)}
                  placeholder="Enter customer name"
                  className="customer-input"
                />
              </div>
              
              <div className="hold-items">
                <label>Items in order:</label>
                <div className="hold-items-list">
                  {order.map(item => (
                    <div key={item.id} className="hold-item">
                      <span>{item.name}</span>
                      <span>Qty: {item.quantity}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="hold-actions">
              <button 
                className="cancel-hold"
                onClick={() => setShowHoldModal(false)}
              >
                Cancel
              </button>
              <button 
                className="confirm-hold"
                onClick={confirmHoldOrder}
                disabled={!holdCustomerName.trim()}
              >
                Hold Order
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Held Orders Modal */}
      {showHeldOrdersModal && (
        <div className="modal-overlay">
          <div className="held-orders-modal">
            <div className="modal-header">
              <h3>Held Orders</h3>
              <button 
                className="close-modal"
                onClick={() => setShowHeldOrdersModal(false)}
              >
                ×
              </button>
            </div>
            
            <div className="held-orders-list">
              {heldOrders.length === 0 ? (
                <div className="no-held-orders">No held orders</div>
              ) : (
                heldOrders.map(heldOrder => (
                  <div key={heldOrder.id} className="held-order-item">
                    <div className="held-order-info">
                      <div className="held-customer-name">{heldOrder.customerName}</div>
                      <div className="held-order-details">
                        <span>{heldOrder.items.length} items</span>
                        <span>{formatCurrency(heldOrder.totalAmount)}</span>
                      </div>
                      <div className="held-order-date">
                        {new Date(heldOrder.timestamp).toLocaleString()}
                      </div>
                    </div>
                    <div className="held-order-actions">
                      <button 
                        className="load-held-order"
                        onClick={() => loadHeldOrder(heldOrder)}
                      >
                        Load
                      </button>
                      <button 
                        className="delete-held-order"
                        onClick={() => deleteHeldOrder(heldOrder.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
            
            <div className="held-orders-actions">
              <button 
                className="close-held-orders"
                onClick={() => setShowHeldOrdersModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="modal-overlay">
          <div className="payment-modal">
            <div className="modal-header">
              <h3>Process Payment</h3>
              <button 
                className="close-modal"
                onClick={() => setShowPaymentModal(false)}
              >
                ×
              </button>
            </div>
            
            <div className="payment-details">
              <div className="payment-total">
                Total: {formatCurrency(totalAmount)}
              </div>
              
              <div className="split-payment-option">
                <label>
                  <input
                    type="checkbox"
                    checked={splitPayment}
                    onChange={(e) => setSplitPayment(e.target.checked)}
                  />
                  Split Payment (Cash + Card)
                </label>
              </div>
              
              {!splitPayment ? (
                <div className="payment-method-selection">
                  <label>Payment Method:</label>
                  <select 
                    value={paymentMethod} 
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    className="payment-method-select"
                  >
                    <option value="cash">Cash</option>
                    <option value="card">Credit/Debit Card</option>
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="mobile">Mobile Payment</option>
                    <option value="uber">Uber</option>
                    <option value="pickme">PickMe</option>
                  </select>
                </div>
              ) : (
                <div className="split-payment-section">
                  <div className="split-payment-row">
                    <label>Cash Amount (LKR):</label>
                    <input
                      type="number"
                      value={cashAmount}
                      onChange={(e) => setCashAmount(e.target.value)}
                      placeholder="0.00"
                      step="0.01"
                      min="0"
                      max={totalAmount}
                      className="split-input"
                    />
                  </div>
                  <div className="split-payment-row">
                    <label>Card Amount (LKR):</label>
                    <input
                      type="number"
                      value={cardAmount}
                      onChange={(e) => setCardAmount(e.target.value)}
                      placeholder="0.00"
                      step="0.01"
                      min="0"
                      max={totalAmount}
                      className="split-input"
                    />
                  </div>
                  <div className={`split-total ${(parseFloat(cashAmount) || 0) + (parseFloat(cardAmount) || 0) < totalAmount ? 'insufficient' : 'sufficient'}`}>
                    <span>Entered Total:</span>
                    <span>{formatCurrency((parseFloat(cashAmount) || 0) + (parseFloat(cardAmount) || 0))}</span>
                    {(parseFloat(cashAmount) || 0) + (parseFloat(cardAmount) || 0) < totalAmount && (
                      <span className="total-warning"> - Insufficient</span>
                    )}
                  </div>
                </div>
              )}
              
              {/* Card Details Section */}
              {(isElectronicPayment(paymentMethod) || splitPayment) && (
                <div className="card-details-section">
                  <h4>Card Payment Details</h4>
                  
                  <div className="card-detail-row">
                    <label>Transaction ID:</label>
                    <input
                      type="text"
                      value={cardDetails.transaction_id}
                      onChange={(e) => setCardDetails(prev => ({
                        ...prev,
                        transaction_id: e.target.value
                      }))}
                      placeholder="Enter transaction ID"
                      className="card-input"
                      required
                    />
                  </div>
                  
                  <div className="card-detail-row">
                    <label>Card Last 4 Digits:</label>
                    <input
                      type="text"
                      value={cardDetails.card_last_four}
                      onChange={(e) => handleCardLastFourChange(e.target.value)}
                      placeholder="1234"
                      maxLength="4"
                      className="card-input"
                      required
                    />
                  </div>
                  
                  <div className="card-detail-row">
                    <label>Card Brand:</label>
                    <select
                      value={cardDetails.card_brand}
                      onChange={(e) => setCardDetails(prev => ({
                        ...prev,
                        card_brand: e.target.value
                      }))}
                      className="card-brand-select"
                      required
                    >
                      <option value="">Select Card Brand</option>
                      {cardBrands.map(brand => (
                        <option key={brand.value} value={brand.value}>
                          {brand.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
              
              {!splitPayment && paymentMethod === 'cash' && (
                <div className="tender-amount-section">
                  <label>Tender Amount (LKR):</label>
                  <input
                    type="number"
                    value={cashAmount}
                    onChange={(e) => setCashAmount(e.target.value)}
                    placeholder="0.00"
                    step="0.01"
                    min={totalAmount}
                    className="tender-input"
                  />
                  {cashAmount && (
                    <div className={`change-amount ${calculateChange() >= 0 ? 'positive' : 'negative'}`}>
                      Change: {formatCurrency(Math.max(0, calculateChange()))}
                      {calculateChange() < 0 && (
                        <span className="insufficient-warning"> (Insufficient)</span>
                      )}
                    </div>
                  )}
                </div>
              )}

              {!splitPayment && isDeliveryPlatform(paymentMethod) && (
                <div className="delivery-platform-info">
                  <div className="platform-notice">
                    <strong>{getPaymentMethodLabel(paymentMethod)} Order</strong>
                    <p>Payment will be processed through {getPaymentMethodLabel(paymentMethod)} platform.</p>
                    <p className="platform-note">
                      The amount will be collected from {getPaymentMethodLabel(paymentMethod)}.
                    </p>
                  </div>
                </div>
              )}
              
              <div className="payment-notes">
                <label>Payment Notes (Optional):</label>
                <textarea
                  value={paymentNotes}
                  onChange={(e) => setPaymentNotes(e.target.value)}
                  placeholder="Add any payment notes..."
                  rows="3"
                  className="notes-textarea"
                />
              </div>
            </div>
            
            <div className="payment-actions">
              <button 
                className="cancel-payment"
                onClick={() => {
                  setShowPaymentModal(false);
                  resetPaymentForm();
                }}
              >
                Cancel
              </button>
              <button 
                className="confirm-payment"
                onClick={processPayment}
                disabled={
                  splitPayment 
                    ? (parseFloat(cashAmount) || 0) + (parseFloat(cardAmount) || 0) < totalAmount ||
                      !cardDetails.transaction_id ||
                      !cardDetails.card_last_four ||
                      cardDetails.card_last_four.length !== 4 ||
                      !cardDetails.card_brand
                    : paymentMethod === 'cash' 
                    ? calculateChange() < 0
                    : isElectronicPayment(paymentMethod)
                    ? !cardDetails.transaction_id || !cardDetails.card_last_four || cardDetails.card_last_four.length !== 4 || !cardDetails.card_brand
                    : false
                }
              >
                {splitPayment ? 'Confirm Split Payment' : `Confirm ${getPaymentMethodLabel(paymentMethod)} Payment`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderPanel;